import { Capacitor } from "@capacitor/core";
// NOTE: Do NOT import @capacitor-community/contacts at module scope.
// Its web bundle registers plugin globals that can throw
// "Attempted to assign to readonly property" in iOS WKWebView at startup.
// Load it lazily inside the native picker function instead.

export type NativePicked = {
  name: string;
  email?: string;
  phone?: string;
  photo?: string; // data URL
  title?: string;
  company?: string;
  birthday?: string; // YYYY-MM-DD ("0001" year if hidden)
  notes?: string[];
};

export function isNativeApp(): boolean {
  try {
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
}

function isWebPickerAvailable(): boolean {
  if (typeof navigator === "undefined" || typeof window === "undefined") return false;
  const nav = navigator as any;
  return !!(nav.contacts && typeof nav.contacts.select === "function" && "ContactsManager" in window);
}

export function contactsPickerSupported(): boolean {
  return isNativeApp() || isWebPickerAvailable();
}

/** Can we render the whole address book in-app (native only)? */
export function contactsFullListSupported(): boolean {
  return isNativeApp();
}

/**
 * Full address book for the multi-select picker. Requires the one-time
 * contacts permission; callers fall back to `pickContacts()` when it's denied.
 */
export async function getAllContacts(limit = 2000): Promise<NativePicked[]> {
  if (!isNativeApp()) throw new Error("Contacts list not available on this device");
  const { Contacts } = await import("@capacitor-community/contacts");
  const perm = await Contacts.requestPermissions();
  if (perm.contacts !== "granted") throw new Error("Contacts permission denied");
  const { contacts } = await Contacts.getContacts({
    projection: { name: true, emails: true, phones: true, organization: true, image: false },
  });
  const out: NativePicked[] = [];
  for (const raw of contacts ?? []) {
    const c: any = raw;
    const name =
      c.name?.display?.trim() ||
      [c.name?.given, c.name?.middle, c.name?.family].filter(Boolean).join(" ").trim();
    if (!name) continue;
    out.push({
      name,
      email: (c.emails ?? []).find((e: any) => e.address?.trim())?.address?.trim() || undefined,
      phone: (c.phones ?? []).find((p: any) => p.number?.trim())?.number?.trim() || undefined,
      title: c.organization?.jobTitle?.trim() || undefined,
      company: c.organization?.company?.trim() || undefined,
    });
    if (out.length >= limit) break;
  }
  return out.sort((a, b) => a.name.localeCompare(b.name));
}

/** Open the native iOS contact picker (single contact). */
async function pickNativeIOS(): Promise<NativePicked[]> {
  const { Contacts } = await import("@capacitor-community/contacts");
  const perm = await Contacts.requestPermissions();
  if (perm.contacts !== "granted") {
    throw new Error("Contacts permission denied");
  }
  const { contact } = await Contacts.pickContact({
    projection: {
      name: true,
      emails: true,
      phones: true,
      image: true,
      organization: true,
      birthday: true,
      note: true,
    },
  });
  if (!contact) return [];
  const c: any = contact;
  const name =
    c.name?.display?.trim() ||
    [c.name?.given, c.name?.middle, c.name?.family].filter(Boolean).join(" ").trim();
  if (!name) return [];

  const emails: Array<{ label?: string; address?: string }> = c.emails ?? [];
  const phones: Array<{ label?: string; number?: string }> = c.phones ?? [];
  const primaryEmail = emails.find((e) => e.address?.trim())?.address?.trim() || undefined;
  const primaryPhone = phones.find((p) => p.number?.trim())?.number?.trim() || undefined;

  const notes: string[] = [];
  const noteText = (c.note ?? "").toString().trim();
  if (noteText) notes.push(noteText.slice(0, 280));
  // Extra emails / phones beyond the primary become memory facts.
  emails.forEach((e) => {
    const v = e.address?.trim();
    if (!v || v === primaryEmail) return;
    notes.push(`${e.label ? `${e.label}: ` : "Email: "}${v}`.slice(0, 280));
  });
  phones.forEach((p) => {
    const v = p.number?.trim();
    if (!v || v === primaryPhone) return;
    notes.push(`${p.label ? `${p.label}: ` : "Phone: "}${v}`.slice(0, 280));
  });

  let birthday: string | undefined;
  const b = c.birthday;
  if (b && (b.month || b.day)) {
    const yyyy = b.year && Number(b.year) > 0 ? String(b.year).padStart(4, "0") : "0001";
    const mm = String(b.month ?? "").padStart(2, "0");
    const dd = String(b.day ?? "").padStart(2, "0");
    if (mm !== "00" && dd !== "00") birthday = `${yyyy}-${mm}-${dd}`;
  }

  return [
    {
      name,
      email: primaryEmail,
      phone: primaryPhone,
      photo: c.image?.base64String
        ? `data:image/jpeg;base64,${c.image.base64String}`
        : undefined,
      title: c.organization?.jobTitle?.trim() || undefined,
      company: c.organization?.company?.trim() || undefined,
      birthday,
      notes: notes.length ? notes.slice(0, 8) : undefined,
    },
  ];
}

/** Web Contact Picker (Android Chrome only). */
async function pickWeb(): Promise<NativePicked[]> {
  const nav = navigator as any;
  const results: Array<{ name?: string[]; email?: string[]; tel?: string[]; icon?: Blob[] }> =
    await nav.contacts.select(["name", "email", "tel"], { multiple: true });
  if (!results?.length) return [];
  const out = await Promise.all(
    results.map(async (r) => {
      const name = (r.name?.[0] || "").trim();
      if (!name) return null;
      const photo = r.icon?.[0] ? await blobToDataUrl(r.icon[0]) : undefined;
      return {
        name,
        email: r.email?.[0]?.trim() || undefined,
        phone: r.tel?.[0]?.trim() || undefined,
        photo,
      } as NativePicked;
    }),
  );
  return out.filter(Boolean) as NativePicked[];
}

/** Unified entry: returns one or more contacts from whichever picker is available. */
export async function pickContacts(): Promise<NativePicked[]> {
  if (isNativeApp()) return pickNativeIOS();
  if (isWebPickerAvailable()) return pickWeb();
  throw new Error("Contacts picker not available on this device");
}

function blobToDataUrl(b: Blob): Promise<string> {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(String(r.result));
    r.onerror = () => rej(r.error);
    r.readAsDataURL(b);
  });
}